import React, { memo, useCallback } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllModeSelector,
  selectedPhotosSelector,
  selectPhotosModeSelector,
  unSelectedPhotosSelector,
} from 'Containers/RocketScan/Header/ActionsCenter/selectors';
import { useRoomsViewFunctions } from 'Context/Project/RoomsView';

import { Photo } from 'Components/RocketScan';
import { setSelectedPhotoIds, setUnSelectedPhotoIds } from 'Containers/RocketScan/Header/ActionsCenter/actions';
import { addOrRemoveFromArray, checkIfPhotoSelected } from 'Utils/helpers';
import { PhotoModal } from 'Containers/PhotoViewCarousel/Models';
import { appendImageVersion } from 'Utils/imageUrls';

export type PendingPhoto = {
  id: string;
  pending: true;
  fileName: string;
  uploadId?: string;
  createdAt?: number;
};

export type GalleryPhoto = PhotoModal | PendingPhoto;

interface Props {
  galleryInstanceId?: string;
  roomId: number;
  albumId: number;
  galleryTitle: string;
  photo: GalleryPhoto;
  isLastItem: boolean;
  photosCount: number;
  editable: boolean;
  fetching: boolean;
  onClickLastThumbnail: (e: any) => void;
  setRefreshOnDelete: (e: any) => void;
}

const PhotoContainer = ({
  galleryInstanceId,
  roomId,
  albumId,
  galleryTitle,
  photo,
  fetching,
  editable,
  isLastItem,
  photosCount,
  onClickLastThumbnail,
  setRefreshOnDelete,
}: Props) => {
  const dispatch = useDispatch();

  const selectPhotosMode = useSelector(selectPhotosModeSelector, areEqual);
  const selectAllMode = useSelector(selectAllModeSelector, areEqual);
  const selectedPhotos = useSelector(selectedPhotosSelector, areEqual);
  const unSelectedPhotos = useSelector(unSelectedPhotosSelector, areEqual);

  const { openPhotoViewModal }: any = useRoomsViewFunctions();

  const isPendingPhoto = (photo as PendingPhoto)?.pending === true;

  if (isPendingPhoto) {
    const { id, fileName } = photo as PendingPhoto;

    return (
      <Photo
        id={id}
        galleryInstanceId={galleryInstanceId}
        roomId={roomId}
        albumId={albumId}
        galleryTitle={galleryTitle}
        pending
        fileName={fileName}
        editable={editable}
        isLastItem={isLastItem}
        photosCount={photosCount}
        notesCount={0}
        createdAt={undefined}
        isBookmarked={false}
        isFlagged={false}
        isPhotoSelected={false}
        onClickThumbnail={() => {}}
        setRefreshOnDelete={setRefreshOnDelete}
      />
    );
  }

  const typedPhoto = photo as PhotoModal;
  const {
    id,
    sizes,
    notes_count: notesCount,
    is_bookmarked: isBookmarked,
    is_flagged: isFlagged,
    created_at: createdAt,
    updated_at: updatedAt,
  } = typedPhoto || {};
  const large = appendImageVersion(sizes?.large, updatedAt || id);

  const onClickThumbnail = useCallback(
    (e) => {
      if (selectPhotosMode && !selectAllMode) {
        dispatch(setSelectedPhotoIds(addOrRemoveFromArray(selectedPhotos, typedPhoto)));

        if (isLastItem && photosCount !== 0) {
          onClickLastThumbnail(e);
        }
      } else if (selectAllMode) {
        dispatch(setUnSelectedPhotoIds(addOrRemoveFromArray(unSelectedPhotos, typedPhoto)));

        if (isLastItem && photosCount !== 0) {
          onClickLastThumbnail(e);
        }
      } else if (isLastItem && photosCount !== 0) {
        onClickLastThumbnail(e);
      } else if (!fetching) {
        openPhotoViewModal(id);
      }
    },
    [
      dispatch,
      fetching,
      isLastItem,
      onClickLastThumbnail,
      openPhotoViewModal,
      photosCount,
      selectPhotosMode,
      selectedPhotos,
      selectAllMode,
      unSelectedPhotos,
      typedPhoto,
    ]
  );

  return (
    <Photo
      id={id}
      galleryInstanceId={galleryInstanceId}
      roomId={roomId}
      albumId={albumId}
      galleryTitle={galleryTitle}
      large={large}
      editable={editable}
      isLastItem={isLastItem}
      photosCount={photosCount}
      notesCount={notesCount}
      createdAt={createdAt}
      isBookmarked={isBookmarked}
      isFlagged={isFlagged}
      isPhotoSelected={checkIfPhotoSelected(
        typedPhoto,
        selectedPhotos,
        unSelectedPhotos,
        selectPhotosMode,
        selectAllMode
      )}
      onClickThumbnail={onClickThumbnail}
      setRefreshOnDelete={setRefreshOnDelete}
    />
  );
};

const PhotoContainerMemo = memo(PhotoContainer, areEqual);

export { PhotoContainerMemo as PhotoContainer };
